/**
 * Parses a Zod validation error from an API error response into a field-level
 * error map. The API may return errors as a JSON-stringified array in the
 * Error `.message` property, or as a structured `response.data` / `data` array.
 *
 * @param error - The error object from a mutation (e.g., `configureSso.error`)
 * @returns A Record mapping field names to human-readable error messages, or
 *          `null` if the error is not a Zod validation error array.
 */
export function parseZodErrors(error: unknown): Record<string, string> | null {
  try {
    if (!error) return null;

    let validationErrors: unknown = null;

    // Attempt 1: Check for structured response data (axios-style or fetch wrapper)
    const err = error as {
      response?: { data?: unknown };
      data?: unknown;
      message?: string;
    };

    const responseData = err.response?.data ?? err.data;
    if (Array.isArray(responseData)) {
      validationErrors = responseData;
    }

    // Attempt 2: Try to JSON.parse the error.message (Zod errors stringified)
    if (!validationErrors && typeof err.message === "string") {
      try {
        const parsed = JSON.parse(err.message);
        if (Array.isArray(parsed)) {
          validationErrors = parsed;
        }
      } catch {
        // Not JSON — not a Zod error
      }
    }

    if (!Array.isArray(validationErrors)) return null;
    if (validationErrors.length === 0) return null;

    const fields: Record<string, string> = {};
    for (const item of validationErrors) {
      if (
        item &&
        typeof item === "object" &&
        Array.isArray(item.path) &&
        item.path.length > 0 &&
        typeof item.message === "string"
      ) {
        const key = item.path[item.path.length - 1];
        if (typeof key === "string") {
          fields[key] = item.message;
        }
      }
    }

    return Object.keys(fields).length > 0 ? fields : null;
  } catch {
    // Never throw — return null for any unexpected error
    return null;
  }
}
